"use client";

import { useState } from "react";
import Link from "next/link";
import { NavigationMenu } from "../NavigationMenu/NavigationMenu";
import { Locale, i18nConfig } from "../../i18nConfig";
import classNames from "classnames/bind";
import styles from "./Header.module.css";
import { usePathname } from "next/navigation";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";

const cx = classNames.bind(styles);

export function Header({
  locale,
  navigationItems,
  claim,
  languages,
  pageTitle,
  navLabel,
  mainMenuLabel,
  languageMenuLabel,
  homepageLabel,
}: {
  locale: Locale;
  navigationItems: { title: string; route: string }[];
  claim: string;
  languages: { isoCode: Locale; title: string }[];
  pageTitle: React.ReactNode;
  navLabel: string;
  mainMenuLabel: string;
  languageMenuLabel: string;
  homepageLabel?: string;
}) {
  const pathname = usePathname();

  /**
   * is the `pathname` only `/`, `de`, or `en`?
   * If yes, assume it's the homepage and show alternate logo.
   */
  const isHomePage = (pathname: string): boolean => {
    const { locales } = i18nConfig;
    const acceptableLanguageSegments = locales.map((locale) => `/${locale}`);
    acceptableLanguageSegments.push("/"); // Adding the root path as a homepage
    return acceptableLanguageSegments.includes(pathname);
  };

  const { scrollY } = useScroll();
  const [headerHidden, setHeaderHidden] = useState(false);
  const [headerAtTop, setHeaderAtTop] = useState(true);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 150) {
      setHeaderAtTop(false);
    } else {
      setHeaderAtTop(true);
    }

    const previous = scrollY.getPrevious() ?? 0;
    if (latest > previous && latest > 150) {
      setHeaderHidden(true);
    } else {
      setHeaderHidden(false);
    }
  });

  return (
    <motion.header
      className={cx("header")}
      data-claim={isHomePage(pathname) ? true : undefined}
      data-variant={headerHidden ? "hidden" : "visible"}
    >
      {!isHomePage(pathname) ? (
        <div className={cx("logoContainer")}>
          <Link className={cx("logoLink")} href={`/${locale}`}>
            <svg
              viewBox="0 0 795 262"
              fill="none"
              className={cx("logo")}
              data-size={!headerAtTop ? "small" : "default"}
            >
              <title>Partner and Partner</title>
              <path
                fill="currentColor"
                fillRule="evenodd"
                d="M319.979 52.77c-5.491 5.866-13.286 9.231-23.398 10.108l38.946 68.104-38.953 68.117c10.108.876 17.912 4.248 23.405 10.108 5.619 5.867 8.428 13.354 8.428 22.46 0 8.609-3.181 15.783-9.551 21.524-6.369 5.861-14.923 8.795-25.656 8.795h-67.04l-.186-130.997.186-130.997h67.04c10.739 0 19.287 2.934 25.656 8.794 6.37 5.742 9.551 12.916 9.551 21.525 0 9.112-2.809 16.599-8.428 22.46Zm-78.836-37.798h52.064c6.117 0 10.859 1.374 14.233 4.115 3.493 2.622 5.24 6.551 5.24 11.788 0 5.236-1.873 9.73-5.619 12.723-3.62 2.993-8.867 4.493-15.727 4.493h-50.191V14.972Zm65.918 203.421c3.746 2.993 5.619 7.234 5.619 12.723 0 5.489-1.747 9.173-5.24 11.788-3.374 2.741-8.116 4.115-14.233 4.115h-52.064v-33.126h50.191c6.867 0 12.107 1.5 15.727 4.493v.007Zm-27.529-19.46h-38.389l-.186-67.931.186-67.93h38.389l38.202 67.93-38.202 67.931Zm98.322-183.96h-42.32V0h99.252v14.973h-41.948l-.193 116.024.193 116.03h41.948v14.966h-99.252v-14.966h42.32l-.185-116.03.185-116.024ZM573.728 123.49h-.02l.013 7.766-.013 7.254h.02l.166 123.483h87.643v-14.966h-72.66v-33.126h65.175v-14.973h-65.175l-.186-60.418h72.812V123.49h-72.812l.186-60.424h65.175V48.092h-65.175V14.974h72.66V0h-87.636l-.173 123.49ZM795 130.997l-.001-.004.001-.003v.007Zm-.001-.004L756.047 62.88c10.116-.876 17.919-4.241 23.412-10.108 5.619-5.86 8.428-13.348 8.428-22.46 0-8.609-3.188-15.783-9.55-21.524C771.967 2.934 763.413 0 752.68 0h-67.047l-.186 130.997.186 130.996h67.047c10.733 0 19.287-2.933 25.657-8.794 6.362-5.741 9.55-12.916 9.55-21.524 0-9.106-2.809-16.593-8.428-22.46-5.493-5.861-13.296-9.232-23.412-10.108l38.952-68.114ZM700.617 14.967h52.063c6.117 0 10.859 1.374 14.233 4.115 3.494 2.621 5.24 6.55 5.24 11.787 0 5.237-1.873 9.73-5.618 12.724-3.62 2.993-8.86 4.493-15.728 4.493h-50.19V14.967Zm65.918 203.421c3.745 2.993 5.618 7.234 5.618 12.723 0 5.489-1.746 9.172-5.24 11.787-3.374 2.742-8.116 4.115-14.233 4.115h-52.063v-33.125h50.19c6.868 0 12.108 1.5 15.728 4.493v.007Zm-27.53-19.46h-38.388l-.186-67.931.186-67.931h38.388l38.203 67.931-38.203 67.931ZM93.959 130.997 143.957 0h14.977l50.004 130.997-49.998 130.996h-14.983L93.959 130.997Zm32.398 43.413 25.092 66.438h.006l25.092-66.438h-50.19Zm55.802-14.967 10.674-28.446-10.674-28.447h-61.421l-10.673 28.447 10.673 28.446h61.421ZM151.442 21.146 126.35 87.583h50.184l-25.092-66.437ZM531.813 0v102.55L468.519 0h-14.984V261.993h14.984l63.294-102.556v102.556h14.984V0h-14.984Zm-.186 130.997-63.108 102.55v-205.1l63.108 102.55Zm-478.07 67.937 17.043.186v-.013c10.109.876 17.913 4.247 23.405 10.108 5.62 5.867 8.428 13.354 8.428 22.46 0 8.608-3.18 15.783-9.55 21.524-6.37 5.861-14.917 8.794-25.657 8.794H.186L0 130.997.186 0h67.04c10.74 0 19.294 2.934 25.657 8.794 6.37 5.741 9.55 12.916 9.55 21.524 0 9.113-2.809 16.6-8.428 22.46-5.492 5.867-13.29 9.233-23.405 10.109l-17.042.185H15.169l-.186 67.931.186 67.931h38.389Zm13.676-183.96H15.169v33.118h50.198c6.86 0 12.107-1.5 15.727-4.493 3.746-2.993 5.619-7.486 5.619-12.723 0-5.237-1.754-9.166-5.247-11.788-3.374-2.74-8.116-4.115-14.233-4.115Zm-.007 232.053c6.117 0 10.86-1.374 14.233-4.115v-.007c3.5-2.615 5.247-6.299 5.247-11.787 0-5.489-1.873-9.731-5.619-12.724-3.62-2.993-8.86-4.493-15.727-4.493H15.17v33.126h52.056Z"
                clipRule="evenodd"
              />
            </svg>
            <span className={cx("logo-text")}>Partner und Partner</span>
          </Link>
        </div>
      ) : (
        <div className={cx("claim")}>
          <Link className={cx("logoLink")} href={`/${locale}`}>
            {claim}
          </Link>
        </div>
      )}
      <div className={cx("inner")}>
        <NavigationMenu
          className={cx("navigation")}
          languages={languages}
          navigationItems={navigationItems}
          locale={locale}
          navLabel={navLabel}
          mainMenuLabel={mainMenuLabel}
          languageMenuLabel={languageMenuLabel}
          homepageLabel={homepageLabel}
        />
        <nav aria-label="Breadcrumb" className={cx("page-title")}>
          {pageTitle}
        </nav>
      </div>
    </motion.header>
  );
}
